import React from 'react';

import {MaxPathLengthOptionResult} from './requests/use-query-result-max-length-options';
import {useRequestQueryResultsSubgraph} from './requests/use-request-query-results-subgraph';

import {GraphNode} from '../../../../../components/graph/interface';
import {StructuredGraph} from '../../../../../components/graph/graph-logic/structured-graph';
import {useLayoutAndCameraProps} from './use-layout-and-camera-props';
import {GraphPresentation, GraphPresentationDetails} from './interface';
import {usePresentationEditedProps} from './use-presentation-edited-props';
import {useGraphSettings} from './use-graph-settings';
import {useUnsavedChangesActions} from './use-unsaved-changes-actions';
import {useHiddenNodes} from './use-hidden-nodes';
import {useHiddenNodesActions} from '../../../../../components/graph/graph-logic/hidden-nodes-actions/use-hidden-nodes-actions';
import {useGraphSelectionProps} from '../../../../../components/graph/graph-logic/use-graph-selection-props';
import {useActionHistory} from './use-action-history';
import {useQueryGraphActions} from './use-query-graph-actions';
import {useUndoRedo} from './use-undo-redo';
import {useOnNodesLoaded} from '../../../../../components/graph/graph-logic/use-on-nodes-loaded';
import {usePathsResultListProps} from '../right-panel/paths-result-list/use-paths-result-list-props';

interface Props {
    selectedQuery: CompletedQuery;
    maxPathLengthOptions: MaxPathLengthOptionResult;
    selectedPresentation: GraphPresentation;
    selectedPresentationDetails: GraphPresentationDetails;
    selectionProps: ReturnType<typeof useGraphSelectionProps>;
    resultsListProps: ReturnType<typeof usePathsResultListProps>;
}

export function useQueryGraphProps({
    selectedQuery,
    maxPathLengthOptions,
    selectedPresentation,
    selectedPresentationDetails,
    selectionProps,
    resultsListProps
}: Props) {
    const actionHistoryProps = useActionHistory();

    const presentationEditedProps = usePresentationEditedProps({
        queryId: selectedQuery.id,
        presentation: selectedPresentation
    });

    const graphSettings = useGraphSettings({
        queryId: selectedQuery.id,
        maxPathLengthOptions,
        selectedPresentation,
        selectedPresentationDetails
    });
    const {viewKind, maxPathLength} = graphSettings;

    const queryGraphRequest = useRequestQueryResultsSubgraph(
        viewKind,
        selectedQuery.id,
        maxPathLength
    );

    const structuredGraph = React.useMemo(() => {
        return queryGraphRequest.data ? new StructuredGraph(queryGraphRequest.data) : undefined;
    }, [queryGraphRequest.data]);

    const hiddenNodesProps = useHiddenNodes({
        queryId: selectedQuery.id,
        selectedPresentation,
        selectedPresentationDetails,
        handlePresentationEdited: presentationEditedProps.handlePresentationEdited,
        structuredGraph
    });

    const layoutAndCameraProps = useLayoutAndCameraProps({
        queryId: selectedQuery.id,
        maxPathLength: Number(maxPathLength),
        viewKind,
        nodes: queryGraphRequest.data?.nodes,
        edges: queryGraphRequest.data?.edges,
        selectedPresentation,
        selectedPresentationDetails,
        handlePresentationEdited: presentationEditedProps.handlePresentationEdited,
        isPresentationEdited: presentationEditedProps.isPresentationEdited,
        isHiddenNodesVisible: hiddenNodesProps.isHiddenNodesVisible,
        isHiddenNode: hiddenNodesProps.isHiddenNode,
        isHiddenEdge: hiddenNodesProps.isHiddenEdge,
        addToHistory: actionHistoryProps.addToHistory
    });

    const hiddenNodesActions = useHiddenNodesActions({
        hiddenNodes: hiddenNodesProps.hiddenNodes,
        selectedNodes: selectionProps.selectedNodes,
        setSelectedNodes: selectionProps.setSelectedNodes,
        setHiddenNodes: hiddenNodesProps.setHiddenNodes,
        structuredGraph,
        isHiddenNodesVisible: hiddenNodesProps.isHiddenNodesVisible,
        toggleIsHiddenNodesVisible: hiddenNodesProps.toggleIsHiddenNodesVisible,
        visibleNodesPositions: layoutAndCameraProps.visibleNodesPositions,
        invisibleNodesPositions: layoutAndCameraProps.invisibleNodesPositions,
        setVisibleNodesPositions: layoutAndCameraProps.setVisibleNodesPositions,
        setInvisibleNodesPositions: layoutAndCameraProps.setInvisibleNodesPositions,
        actionHistoryProps,
        presentationEditedProps,
        zoom: layoutAndCameraProps.zoom,
        pan: layoutAndCameraProps.pan
    });

    const {clearUnsavedChanges, setUnsavedChanges, changesClearedOrSavedCounter} =
        useUnsavedChangesActions({
            presentationEditedProps,
            graphSettings,
            layoutAndCameraProps,
            hiddenNodesProps,
            structuredGraph,
            actionHistoryProps
        });

    const graphActions = useQueryGraphActions({
        layoutAndCameraProps,
        actionHistoryProps,
        presentationEditedProps,
        graphSettings,
        maxPathLengthOptions,
        selectionProps,
        resultsListProps
    });

    const {canFetchNodeStructure} = useOnNodesLoaded({
        queryGraphRequest,
        onLoaded: (nodes: GraphNode[], previousLoadedNodes?: GraphNode[]) => {
            const newSelection = selectionProps.onNodesLoaded(nodes);
            if (
                newSelection &&
                JSON.stringify(newSelection) !== JSON.stringify(selectionProps.selectedNodes)
            ) {
                graphActions.selectionChangedEffect(newSelection);
            }
            layoutAndCameraProps.onNodesLoaded(nodes, previousLoadedNodes);
        }
    });

    const undoRedoProps = useUndoRedo({
        actionHistoryProps,
        layoutAndCameraProps,
        graphActions,
        presentationEditedProps,
        graphSettings,
        selectionProps,
        hiddenNodesActionsProps: hiddenNodesActions,
        resultsListProps
    });

    return {
        queryGraphRequest,
        structuredGraph,
        layoutAndCameraProps,
        graphSettings,
        presentationEditedProps,
        clearUnsavedChanges,
        setUnsavedChanges,
        changesClearedOrSavedCounter,
        hiddenNodesProps,
        hiddenNodesActions,
        actionHistoryProps,
        graphActions,
        undoRedoProps,
        canFetchNodeStructure
    };
}
